import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import MeetingCard from './MeetingCard';
import BookSlotModal from './BookSlotModal';
import ApplyToSpeakModal from './ApplyToSpeakModal';
import { getMeetings, getCurrentMember, bookSlot, cancelSlotBooking, createMeeting } from '../services/api';
import { showToast } from '../hooks/useToast';
import usePageTitle from '../hooks/usePageTitle';
import Larry from './Larry';

const PAGE_SIZE = 50;
const today = new Date().toISOString().split('T')[0];

export default function Calendar() {
  usePageTitle('Schedule');
  const [member, setMember] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slotError, setSlotError] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshMeetings = async () => {
    try {
      const res = await getMeetings('all', 200);
      if (res?.meetings) setMeetings(res.meetings);
    } catch (err) {
      setSlotError(err.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [memberRes, meetingsRes] = await Promise.all([
        getCurrentMember().catch(() => null),
        getMeetings('all', 200),
      ]);
      if (memberRes?.member) setMember(memberRes.member);
      if (meetingsRes?.meetings) { setMeetings(meetingsRes.meetings); setPage(0); }
    } catch (err) {
      console.error('[Calendar] Load error:', err);
      setError(err.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotClick = async (slot) => {
    setSlotError(null);
    if (slot.status === 'open') {
      setBookingSlot(slot);
      return;
    }
    if (slot.speaker_id === member?.member_id) {
      try {
        await cancelSlotBooking(slot.slot_id);
        await refreshMeetings();
      } catch (err) {
        setSlotError(err.message || 'Failed to cancel booking');
      }
    }
  };

  const handleBookingConfirm = async (presentation_title, synopsis) => {
    await bookSlot(bookingSlot.slot_id, { presentation_title, synopsis });
    setBookingSlot(null);
    await refreshMeetings();
    showToast('Slot booked!');
  };

  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  })();

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);
    try {
      await createMeeting(newDate, newTitle.trim() || undefined);
      setShowCreateModal(false);
      setNewDate('');
      setNewTitle('');
      await refreshMeetings();
      showToast('Meeting created');
    } catch (err) {
      setCreateError(err.message || 'Failed to create meeting');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-10 w-52 rounded-xl" style={{ background: 'var(--bg-input)' }} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="animate-pulse h-40 rounded-2xl" style={{ background: 'var(--bg-input)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-2xl mb-3" style={{ color: 'var(--text-faint)' }}>⚠</p>
        <p className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>{error}</p>
        <button onClick={loadData} className="btn-bouncy text-sm px-5 py-2 mt-4">Try Again</button>
      </div>
    );
  }

  const canBook = !!member;
  const canCancel = !!member;

  const bookedMeetingIds = meetings
    .filter(m => m.slots?.some(s => s.speaker_id === member?.member_id))
    .map(m => m.meeting_id);

  const totalPages = Math.ceil(meetings.length / PAGE_SIZE);
  const pagedMeetings = meetings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const groupedMeetings = pagedMeetings.reduce((acc, meeting) => {
    const date = new Date(meeting.meeting_date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    if (!acc[key]) acc[key] = { label, meetings: [] };
    acc[key].meetings.push(meeting);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {showApplyModal && (
        <ApplyToSpeakModal
          bookedMeetingIds={bookedMeetingIds}
          onClose={() => setShowApplyModal(false)}
          onBooked={() => {
            setShowApplyModal(false);
            fetchedRef.current = false;
            loadData();
          }}
        />
      )}
      {bookingSlot && (
        <BookSlotModal
          meetingDate={bookingSlot.meeting_date}
          onConfirm={handleBookingConfirm}
          onClose={() => setBookingSlot(null)}
        />
      )}

      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 space-y-4"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
          >
            <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              New Meeting
            </h3>
            <form onSubmit={handleCreateMeeting} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Date <span style={{ color: 'var(--accent)' }}>*</span>
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={minDate}
                  required
                  className="w-full px-3 py-2 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Title <span className="opacity-50">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Weekly Meeting #42"
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                />
              </div>
              {createError && (
                <p className="text-sm" style={{ color: 'var(--color-danger)' }}>⚠ {createError}</p>
              )}
              <div className="flex gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="pill-tab text-sm"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-bouncy text-sm px-5 py-2"
                >
                  {creating ? 'Creating…' : 'Create Meeting'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {slotError && (
        <div
          className="px-4 py-3 rounded-xl text-sm font-medium"
          style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}
        >
          ⚠ {slotError}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-end">
        {member && member.role !== 'admin' && (
          <button
            onClick={() => setShowApplyModal(true)}
            className="btn-bouncy text-sm px-4 py-2"
          >
            Apply to Speak
          </button>
        )}
        {member?.role === 'admin' && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              onClick={() => { setShowCreateModal(true); setCreateError(null); }}
              className="btn-bouncy text-sm px-4 py-2"
            >
              + New Meeting
            </button>
            <Link
              to="/admin?tab=meetings"
              className="text-xs font-medium transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              Manage in Admin →
            </Link>
          </div>
        )}
      </div>


      {/* Empty state */}
      {meetings.length === 0 ? (
        <div
          className="card-paper p-8 flex flex-col items-center text-center gap-3"
        >
          <Larry mood="thinking" size={64} message="No meetings scheduled yet." />
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
            {member?.role === 'admin'
              ? 'Create the first meeting to get the schedule rolling.'
              : 'Check back soon — meetings will appear here when scheduled.'}
          </p>
          {member?.role === 'admin' && (
            <button
              onClick={() => { setShowCreateModal(true); setCreateError(null); }}
              className="btn-bouncy text-sm px-5 py-2 font-semibold mt-1"
            >
              Create a meeting →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8 stagger-in">
          {Object.entries(groupedMeetings).sort(([a], [b]) => b.localeCompare(a)).map(([key, { label, meetings: monthMeetings }]) => (
            <section key={key}>
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className="w-0.5 h-3 rounded-full flex-shrink-0"
                  style={{ background: 'linear-gradient(180deg, var(--accent) 0%, rgba(45,111,224,0) 100%)' }}
                />
                <h3
                  className="font-mono text-xs font-medium uppercase tracking-[0.2em]"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {label}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {monthMeetings.map((meeting) => (
                  <MeetingCard
                    key={meeting.meeting_id}
                    meeting={meeting}
                    currentMemberId={member?.member_id}
                    canBook={canBook && meeting.meeting_date >= today}
                    canCancel={canCancel}
                    onSlotClick={handleSlotClick}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-30 transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
              >
                ← Prev
              </button>
              <span className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-30 transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
